<template>
  <div>
    <div class="air__utils__heading">
      <div class="row">
        <div class="col-md-12">
          <h5>Fee Summary</h5>
        </div>
      </div>
    </div>
    <div class="row justify-content-center">
      <div class="col-xl-12">
        <div class="row">
          <!-- Session Select -->
          <div class="col-md-2 col-12 mb-2">
            <a-select
              size="large"
              v-model="selectedSession"
              optionFilterProp="children"
              style="width: 100%"
              @change="handleSessionChange"
              :filterOption="false"
              :defaultValue="currentSession ? currentSession.id : ''"
            >
              <template v-for="schoolSession in schoolSessions">
                <a-select-option
                  :value="schoolSession.id"
                  :key="schoolSession.id"
                >
                  {{ schoolSession.name }}
                  <small class="text-primary ml-2">
                    {{
                      currentSession.id === schoolSession.id ? "Current" : ""
                    }}
                  </small>
                </a-select-option>
              </template>
            </a-select>
          </div>
          <!-- Class Select -->
          <div class="col-md-2 col-12 mb-2">
            <a-spin :spinning="classSpinning">
              <a-select
                size="large"
                v-model="selectedClass"
                placeholder="All Classes"
                optionFilterProp="children"
                style="width: 100%"
                @change="handleClassSelect"
                :filterOption="false"
                :defaultValue="undefined"
              >
                <a-select-option :value="undefined"
                  >All Classes</a-select-option
                >
                <template v-for="schoolClass in schoolClasses">
                  <a-select-option
                    :value="schoolClass.id"
                    :key="schoolClass.id"
                  >
                    {{ schoolClass.name }}
                  </a-select-option>
                </template>
              </a-select>
            </a-spin>
          </div>
          <!-- Section Select -->
          <div class="col-md-2 col-12 mb-2">
            <a-spin :spinning="sectionSpinning">
              <a-select
                size="large"
                v-model="selectedSection"
                placeholder="All Sections"
                optionFilterProp="children"
                style="width: 100%"
                @change="handleSectionChange"
                :filterOption="false"
                :defaultValue="undefined"
                :disabled="!selectedClass"
              >
                <a-select-option :value="undefined"
                  >All Sections</a-select-option
                >
                <template v-for="section in sections">
                  <a-select-option :value="section.id" :key="section.id">
                    {{ section.name }}
                  </a-select-option>
                </template>
              </a-select>
            </a-spin>
          </div>
          <!-- Fee Select -->
          <div class="col-md-4 col-12 mb-2">
            <a-spin :spinning="feesSpinning">
              <a-select
                size="large"
                v-model="selectedFee"
                placeholder="Select Fee"
                optionFilterProp="children"
                style="width: 100%"
                @change="handleFeeChange"
                :filterOption="false"
                :defaultValue="undefined"
              >
                <a-select-option :value="undefined"
                >Select Fee</a-select-option
                >
                <template v-for="fee in schoolFees">
                  <a-select-option :value="fee.id" :key="fee.id">
                    {{ fee.name }}
                  </a-select-option>
                </template>
              </a-select>
            </a-spin>
          </div>
          <div class="col-md-2 col-12 mb-2">
            <a-spin :spinning="sectionSpinning">
              <a-select
                size="large"
                v-model="selectedStatus"
                placeholder="All Status"
                optionFilterProp="children"
                style="width: 100%"
                @change="handleStatusChange"
                :filterOption="false"
                :defaultValue="undefined"
                :disabled="!selectedClass"
              >
                <a-select-option :value="undefined">All Status</a-select-option>

                <a-select-option :value="1" :key="1">Paid</a-select-option>
                <a-select-option :value="0" :key="0">Unpaid</a-select-option>
              </a-select>
            </a-spin>
          </div>
          <!-- Student Search -->
          <div class="col-md-6 col-12 mb-2">
            <div class="text-right mb-3">
              <a-input-search
                placeholder="Search Students.."
                size="large"
                v-model="search"
                @search="searchStudents"
              />
            </div>
          </div>
        </div>
        <div class="card">
          <div class="card-body">
            <students-fee-summary
              :data="data"
              :loading="loading"
              :session="selectedSession"
              @updateTable="handleUpdateTable"
            />
          </div>
        </div>

        <div class="text-right">
            <a-pagination
            showSizeChanger
            @showSizeChange="onShowSizeChange"
            :total="meta.total"
            :pageSize.sync="pageSize"
            v-model="current"
            @change="onChange"
          >
          </a-pagination>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import studentsFeeSummary from '@/components/feemanagement/summary/studentsfeesummary'

import { debounce, find } from 'lodash'
import { mapState } from 'vuex'

export default {
  components: {
    studentsFeeSummary,
  },
  mounted: function () {
    this.$http
      .get('/api/session')
      .then(({ data }) => {
        this.schoolSessions = data.data
        const activeSession = find(data.data, ['is_current', 1])
        if (activeSession) {
          this.selectedSession = activeSession.id
        } else if (data.data && data.data.length > 0) {
          this.selectedSession = data.data[0].id
        }
        this.getClasses()
        this.getSchoolFees()
        this.getStudentsFeeSummary()
      })
      .catch((error) => {
        console.log(error)
      })

    window.Echo.private('GenerateFee.' + this.user.user.id).listen(
      '.StudentFeesGenerated',
      (e) => {
        this.handleUpdateTable()
      }
    )
  },
  created() {
    this.searchStudents = debounce(this.searchStudents, 500)
  },
  destroyed: function () {
    window.Echo.leave('GenerateFee.' + this.user.user.id)
  },
  computed: {
    currentSession: function () {
      const session = find(this.schoolSessions, ['is_current', 1])
      return session
    },
    ...mapState(['user']),
  },
  watch: {
    currentSession: function (val) {
      this.selectedSession = val.id
    },
  },
  data: function () {
    return {
      loading: true,
      visible: false,
      data: [],
      meta: {},
      schoolClasses: [],
      sections: [],
      schoolSessions: [],
      current: 1,
      pageSize: 10,
      selectedClass: undefined,
      selectedSection: undefined,
      selectedSession: undefined,
      sectionSpinning: false,
      classSpinning: false,
      search: null,
      schoolFees: [],
      feesSpinning: false,
      selectedFee: undefined,
      selectedStatus: undefined,

    }
  },
  methods: {
    getStudentsFeeSummary: function (isSearch) {
      this.loading = true

      let page = this.current
      if (isSearch) {
        page = 1
        this.current = 1
      }

      this.$http
        .get('/api/student_fee_summary', {
          params: {
            limit: this.pageSize,
            page: page,
            search: this.search,
            class_id: this.selectedClass,
            section_id: this.selectedSection,
            session_id: this.selectedSession,
            fee_id: this.selectedFee,
            status: this.selectedStatus,
          },
        })
        .then(({ data }) => {
          this.loading = false
          this.data = data.data
          this.meta = data.data.meta
          console.log(this.meta)
        })
    },
    searchStudents: function (value) {
      this.search = value
      this.getStudentsFeeSummary(true)
    },
    handleCreated: function () {
      this.getStudentsFeeSummary()
    },
    handleUpdateTable: function () {
      this.getStudentsFeeSummary()
    },
    onShowSizeChange(current, pageSize) {
      this.pageSize = pageSize
      this.current = current
      this.getStudentsFeeSummary()
    },
    onChange(current) {
      this.current = current
      this.getStudentsFeeSummary()
    },

    getClasses: function () {
      this.classSpinning = true
      this.$http
        .get('/api/class', {
          params: {
            session_id: this.selectedSession,
          },
        })
        .then(({ data }) => {
          this.schoolClasses = data.data
          this.classSpinning = false
        })
        .catch((error) => {
          console.log(error)
        })
    },
    handleClassSelect(value) {
      this.selectedSection = undefined
      if (value) {
        this.sectionSpinning = true
        this.getClassSections(value)
      }
      this.getStudentsFeeSummary()
    },
    getClassSections(value) {
      this.$http
        .get(`/api/section/class/${value}`, {
          params: {
            session_id: this.selectedSession,
          },
        })
        .then(({ data }) => {
          this.sections = data.data
          this.sectionSpinning = false
        })
        .catch((error) => {
          console.log(error)
        })
    },
    handleSectionChange(value) {
      if (value) {
        this.selectedSection = value
        this.getStudentsFeeSummary()
      } else {
        this.selectedSection = undefined
        this.getStudentsFeeSummary()
      }
    },
    getSchoolFees() {
      this.$http
        .get(`/api/fee`, {
          params: {
            limit: 45,
            page: 1,
          },
        })
        .then(({ data }) => {
          this.schoolFees = data.data
          // this.feesSpinning = false
        })
        .catch((error) => {
          console.log(error)
        })
    },
    handleFeeChange(value) {
      if (value) {
        this.selectedFee = value
        this.getStudentsFeeSummary()
      } else {
        this.selectedFee = undefined
        this.getStudentsFeeSummary()
      }
    },
    handleStatusChange(value) {
      if (value !== undefined || value !== null) {
        this.selectedStatus = value
        this.getStudentsFeeSummary()
      } else {
        this.selectedStatus = undefined
        this.getStudentsFeeSummary()
      }
    },
    handleSessionChange(value) {
      if (value) {
        this.selectedSession = value
      } else {
        this.selectedSession = this.currentSession
      }
      this.selectedSection = undefined
      this.selectedClass = undefined

      this.getClasses()
      this.getStudentsFeeSummary()
    },
  },
}
</script>
